import { Lock } from "@mui/icons-material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  STATUS_COLORS_CASH,
  STATUS_LABELS_CASH,
} from "../../data/models/cash/CashModel";
import { OrderModel } from "../../data/models/order/OrderModel";
import { useUserData } from "../../data/providers/UserDataProvider";
import { maskId } from "../../utils/StringTool";
import { useCashSession } from "../hooks/useCashSession";
import {
  InvoicePrint,
  InvoicePrintProps,
} from "../modules/components/InvoicePrint";
import CashOrdersTable from "./components/CashOrdersTable";

export default function CashierMovements() {
  const navigate = useNavigate();

  const {
    cashSession,
    loading,
    error,
    updateOpeningBalance,
    closeCashSession,
  } = useCashSession();
  const userData = useUserData()!;

  const [openOpenDialog, setOpenOpenDialog] = useState(false);
  const [openCloseDialog, setOpenCloseDialog] = useState(false);
  const [cashValue, setCashValue] = useState<number | "">("");

  const [dataToPrint, setDataToPrint] = useState<InvoicePrintProps | null>(
    null
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!dataToPrint) return;

    printInSameWindow(dataToPrint.printMode !== "PRE_INVOICE");
  }, [dataToPrint]);

  if (loading) {
    return (
      <Stack sx={{ p: 2 }} gap={2}>
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
      </Stack>
    );
  }

  if (error || !cashSession) {
    return <Typography color="error">Error cargando la caja</Typography>;
  }

  const formatCOP = (value: number | string) => {
    if (!value) return "";
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(Number(value));
  };

  const printInSameWindow = (goBack?: boolean) => {
    const source = contentRef.current;
    const preview = document.getElementById("printPreview");
    if (!source || !preview) return;
    setPrinting(true);
    preview.innerHTML = source.innerHTML;
    preview.style.display = "block";
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        setPrinting(false);
      }, 800);
    }, 300);
  };

  const printCopy = async (orderData: OrderModel) => {
    setDataToPrint(buildInvoicePrintData("COPY", orderData));
  };

  const buildInvoicePrintData = (
    printMode: "ORIGINAL" | "COPY" | "PRE_INVOICE",
    orderData: OrderModel
  ): InvoicePrintProps | null => {
    if (!orderData) return null;
    const company = userData?.data.currentCompany;

    const companyInfo = {
      name: company.companyName,
      nit: company.identification,
      address: company.address,
      phone: company.phoneNumber,
      logoUrl: company.imageUrl,
    };

    // const customer = selectedCustomer;

    const dateObj = new Date(orderData.createdAt);
    const date = dateObj.toLocaleDateString("es-CO");
    const hour = dateObj.toLocaleTimeString("es-CO", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return {
      orderId: orderData.id,
      items: orderData.items.map((i) => ({
        name: i.product.name,
        qty: i.quantity,
        price: i.product.salePrice,
        comments: i.comments,
      })),
      subtotal: orderData.subtotal,
      discount: orderData.discountValue,
      total: orderData.total,
      company: companyInfo,
      customer: {},
      module: orderData.moduleName!,
      date,
      hour,
      paymentMethod: orderData.paymentMethod!,
      paidAmount: null,
      change: null,
      fiscalNumber: orderData.fiscalNumber,
      printMode,
    };
  };

  return (
    <Stack sx={{ mb: 2, p: 2 }} gap={3}>
      <Box sx={{ height: 4 }}>
        {loading && <LinearProgress sx={{ height: 4 }} />}
      </Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ borderRadius: "12px" }}
        >
          Volver
        </Button>
        <Typography variant="h5" fontWeight={700}>
          Movimientos de Caja
        </Typography>
      </Stack>

      <Box
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack>
            <Typography fontWeight={700} fontSize={20}>
              {maskId(cashSession?.id)}
            </Typography>
            {/* <Typography color="text.secondary" fontSize={14}>
              Abierta por {cashSession?.openedBy}
            </Typography> */}
            <Typography color="text.secondary" fontSize={13}>
              {cashSession?.openedAt.toLocaleString()}
            </Typography>
          </Stack>

          <Chip
            label={STATUS_LABELS_CASH[cashSession!.status]}
            sx={{
              bgcolor: STATUS_COLORS_CASH[cashSession!.status],
              color: "white",
              fontWeight: 700,
              px: 2,
            }}
          />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={6} md={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack alignItems="center" gap={0.5}>
                <AccountBalanceWalletIcon color="primary" />
                <Typography variant="caption">Base inicial</Typography>
                <Typography fontWeight={700}>
                  ${cashSession?.openingBalance.toLocaleString()}
                </Typography>
              </Stack>
            </Box>
          </Grid>

          <Grid item xs={6} md={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack alignItems="center" gap={0.5}>
                <TrendingUpIcon color="success" />
                <Typography variant="caption">Total vendido</Typography>
                <Typography fontWeight={700}>
                  ${cashSession?.reportedBalance.toLocaleString()}
                </Typography>
              </Stack>
            </Box>
          </Grid>

          {/* <Grid item xs={6} md={6}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack alignItems="center" gap={0.5}>
                <SwapHorizIcon color="warning" />
                <Typography variant="caption">Diferencia</Typography>
                <Typography
                  fontWeight={700}
                  color={
                    cashSession?.difference === 0
                      ? "success.main"
                      : "error.main"
                  }
                >
                  ${cashSession?.difference.toLocaleString()}
                </Typography>
              </Stack>
            </Box>
          </Grid> */}

          {/* <Grid item xs={6} md={3}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack alignItems="center" gap={0.5}>
                <Typography variant="caption">En caja</Typography>
                <Typography fontWeight={700}>
                  ${cashSession?.closingBalance.toLocaleString()}
                </Typography>
              </Stack>
            </Box>
          </Grid> */}
        </Grid>
        <Divider sx={{ my: 3 }} />
        {cashSession.status === "OPENING" && (
          <Stack direction="row" justifyContent="flex-end" gap={2}>
            {cashSession.openingBalance === 0 && (
              <Button
                variant="contained"
                startIcon={<AccountBalanceWalletIcon />}
                onClick={() => {
                  setCashValue("");
                  setOpenOpenDialog(true);
                }}
                sx={{
                  fontWeight: 600,
                  borderRadius: "12px",
                  px: 3,
                }}
              >
                Agregar Base Inicial
              </Button>
            )}

            <Button
              variant="contained"
              color="warning"
              startIcon={<Lock />}
              onClick={() => {
                setCashValue("");
                setOpenCloseDialog(true);
              }}
              sx={{
                fontWeight: 600,
                borderRadius: "12px",
                px: 3,
              }}
            >
              Cierre de Caja
            </Button>
          </Stack>
        )}
      </Box>

      <CashOrdersTable orders={cashSession.orders} printCopy={printCopy} />

      <Dialog open={openOpenDialog} onClose={() => setOpenOpenDialog(false)}>
        <DialogTitle>Apertura de Caja</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Monto en efectivo"
            type="text"
            value={cashValue === "" ? "" : formatCOP(cashValue)}
            onChange={(e) => {
              const cleanValue = e.target.value.replace(/\D/g, "");
              setCashValue(cleanValue === "" ? "" : Number(cleanValue));
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOpenDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={async () => {
              await updateOpeningBalance({
                sessionId: cashSession.id,
                openingBalance: Number(cashValue),
              });
              setOpenOpenDialog(false);
            }}
            disabled={!cashValue}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCloseDialog} onClose={() => setOpenCloseDialog(false)}>
        <DialogTitle>Cierre de Caja</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Monto final (Efectivo, transferencias, otros)"
            type="text"
            value={cashValue === "" ? "" : formatCOP(cashValue)}
            onChange={(e) => {
              const cleanValue = e.target.value.replace(/\D/g, "");
              setCashValue(cleanValue === "" ? "" : Number(cleanValue));
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCloseDialog(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={async () => {
              await closeCashSession({
                sessionId: cashSession.id,
                reportedClosingAmount: Number(cashValue),
              });
              setOpenCloseDialog(false);
            }}
            disabled={!cashValue}
          >
            Confirmar Cierre
          </Button>
        </DialogActions>
      </Dialog>

      <div style={{ display: "none" }}>
        {dataToPrint && <InvoicePrint ref={contentRef} {...dataToPrint} />}
      </div>

      {printing && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            bgcolor: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(2px)",
            pointerEvents: "auto",
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <LinearProgress sx={{ width: 220, height: 6, borderRadius: 3 }} />
            <Typography color="white" fontWeight={600}>
              Modo impresión...
            </Typography>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
